import React, { useState, useEffect, useCallback, useMemo } from "react";
import { DataGrid } from '@mui/x-data-grid'
import { useNavigate, useLocation } from "react-router-dom";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faTrash } from "@fortawesome/free-solid-svg-icons"; // import edit e trash icon
import DeleteConfirmation from "./../../components/DeleteConfirmation.js";
import { useHeader } from './../../components/HeaderContext';
import { useAxiosPrivate } from "./../../hooks/useAxiosPrivate.js";
import { encryptId } from './../../components/utils_react.jsx'
import useAuth from "./../../hooks/useAuth.js";
import OkModal from "./../../components/OkModal.js";
import { useAlert } from './../../components/AlertContext.jsx';
import "./../../App.css";

const Products = () => {

    // Constants and hooks
    const baseURL = process.env.REACT_APP_API_BASE_URL;
    const axiosPrivate = useAxiosPrivate();
    const { auth } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const { setHeader } = useHeader();
    const { triggerAlert } = useAlert();
    const ReturnPage = "/products";


    // State management
    const [loading, setLoading] = useState(false);
    const [rows, setRows] = useState([]);
    const [totalRows, setTotalRows] = useState(0);
    const [showOkModal, setShowOkModal] = useState(false);
    const [okMessage, setOkMessage] = useState("");
    const [typeOkModal, setTypeOkModal] = useState("success");



    // Important: Use a ref to ensure we always have the latest pagination state
    // This solves the issue with ViewRow always having pagination.page = 0
    const paginationRef = React.useRef({
        page: location.state?.pagination?.page || 0,
        pageSize: location.state?.pagination?.pageSize || 20,
        sortField: location.state?.pagination?.sortField || "code",
        sortOrder: location.state?.pagination?.sortOrder || "asc"
    });

    // Pagination state - initialize from location state if available
    const [pagination, setPagination] = useState(paginationRef.current);


    // Update ref whenever pagination state changes
    useEffect(() => {
        paginationRef.current = pagination;
    }, [pagination]);


    // Set header title
    useEffect(() => {
        setHeader("Products");
    }, [setHeader]);


    const [type, setType] = useState(null);
    const [id, setId] = useState(null);         // usado no dialogo de confirmar delete e voltar
    const [displayConfirmationModal, setDisplayConfirmationModal] = useState(false);
    const [deleteMessage, setDeleteMessage] = useState(null);

    // Handle the displaying of the modal based on type and id
    const showDeleteModal = (type, id) => {
        setId(id);
        console.log(id);
        setDeleteMessage(`Are you sure you want to delete the record: ${type}?`);

        setDisplayConfirmationModal(true);
    };

    // Hide the modal
    const hideConfirmationModal = () => {
        setDisplayConfirmationModal(false);
    };

    const handleShowOkModal = (message, type) => {
        setOkMessage(message);
        setTypeOkModal(type);
        setShowOkModal(true);
    }

    const handleHideOkModal = () => {
        setShowOkModal(false);
    };




    // Memorize columns to prevent unnecessary re-renders
    const columns = useMemo(() => [
        {
            field: 'actions',
            headerName: '',
            disableColumnMenu: true,
            width: 60,
            renderCell: (params) => (
                <div>
                    <FontAwesomeIcon
                        icon={faEye}
                        style={{ cursor: 'pointer', marginRight: '8px' }}
                        onClick={() => ViewRow(params.row.id)}
                    />
                    {/*
                    <FontAwesomeIcon
                        icon={faTrash}
                        style={{ cursor: 'pointer' }}
                        onClick={() => deleteRow(params.row.code, params.row.id)}
                    />
                    */ }
                </div>
            ),
        },
        { field: 'product_id', headerName: 'ID', disableColumnMenu: true },
        { field: 'code', headerName: 'Code', width: 200, headerClassName: 'super-app-theme--header', disableColumnMenu: true },
        { field: 'description', headerName: 'Description', width: 300, disableColumnMenu: true },
    ], []);







    const ViewRow = (param1) => {
        // Always use the ref for the most up-to-date pagination values
        const currentPagination = paginationRef.current;

        console.log("ViewRow. Pagination.page: ", currentPagination.page);
        const encryptedId = encryptId(param1);

        navigate(`/products_view/${encryptedId}`, {
            state: {
                pagination: {
                    page: currentPagination.page,
                    pageSize: currentPagination.pageSize,
                    sortField: currentPagination.sortField,
                    sortOrder: currentPagination.sortOrder
                },
                from: 'products'
            }
        });
    };


    useEffect(() => {
        console.log("Pagination state updated:", pagination);
    }, [pagination]);


    function deleteRow(param1, param2) {
        console.log("products.js > deleteRow > param1: ", param1, "param2: ", param2);
        showDeleteModal(param1, param2)
    }



    const paginationModel = {
        page: pagination.page,
        pageSize: pagination.pageSize
    };

    // Use useCallback to memoize fetch function
    const fetchProducts = useCallback(async () => {
        try {
            setLoading(true);
            const result = await axiosPrivate.get(
                `${baseURL}/products_page?page=${pagination.page + 1}&limit=${pagination.pageSize}&sortfield=${pagination.sortField}&sortorder=${pagination.sortOrder}`
            );

            if (result.status === 200) {
                setRows(result.data.result.map(row => ({ id: row.product_id, ...row })));
                setTotalRows(result.data.total_count);
            } else {
                triggerAlert(result.data.Error, 'error');
            }
        } catch (error) {
            console.error('Failed to fetch products:', error);
            triggerAlert('Failed to fetch products', 'error');
        } finally {
            setLoading(false);
        }
    }, [pagination, axiosPrivate, baseURL]);


    const handleSubmitDelete = async (id) => {
        console.log(id);
        try {
            setDisplayConfirmationModal(false);
            const res1 = await axiosPrivate.get(`${baseURL}/products_allowdelete/` + id);
            let total_count = res1.data.total_count;
            console.log("total_count: ", total_count);

            if (total_count >= 1) {
                handleShowOkModal("Product in use. Record can't be deleted!", "warning");
            } else {
                try {
                    const res2 = await axiosPrivate.delete(`${baseURL}/products/` + id);
                    let message = `Record deleted!`;
                    triggerAlert(message, 'success');
                    console.log(res2);
                    fetchProducts();
                } catch (err) {
                    console.log('Error fetching data:', err);
                    console.log(err.response.data.err.originalError.info.message);
                    let message = `Erro na gravação!  \n ${err.response.data.err.originalError.info.message}`;
                    triggerAlert(message, 'error');
                }
            }
        } catch (err) {
            console.log('Error fetching data:', err);
            let message = `Erro na gravação!  \n ${err.response.data.err.originalError.info.message}`;
            triggerAlert(message, 'error');
        }
    };





    // Effect to restore pagination state from location
    useEffect(() => {
        if (location.state?.pagination) {
            const newPagination = {
                page: location.state.pagination.page,
                pageSize: location.state.pagination.pageSize,
                sortField: location.state.pagination.sortField || "code",
                sortOrder: location.state.pagination.sortOrder || "asc"
            };

            setPagination(newPagination);
            // Also update the ref to keep them in sync
            paginationRef.current = newPagination;
        }
    }, [location.state]);




    // Effect to load products when pagination changes
    useEffect(() => {
        const controller = new AbortController();
        fetchProducts();
        return () => controller.abort();
    }, [fetchProducts]);



    // Update handlePageChange to keep everything in sync
    const handlePaginationChange = useCallback((newPagination) => {
        setPagination(prev => ({
            ...prev,
            ...newPagination
        }));
    }, []);



    // Handler for sorting
    const handleSortModelChange = (newSortModel) => {
        if (newSortModel.length > 0) {
            const { field, sort } = newSortModel[0];
            const newPagination = {
                ...pagination,
                page: 0,  // Reset to first page when sorting changes
                sortField: field,
                sortOrder: sort
            };

            setPagination(newPagination);
            paginationRef.current = newPagination;
        } else {
            const newPagination = {
                ...pagination,
                sortField: "code",
                sortOrder: "asc"
            };

            setPagination(newPagination);
            paginationRef.current = newPagination;
        }
    };


    const handleBtnNew = async () => {
        console.log("groups.js > handleBtnNew ");
        navigate(`/groups_add`);
    };


    // Add this useEffect to log state changes
    useEffect(() => {
        console.log("Current pagination state:", pagination);
    }, [pagination]);

    return (
        <div className="mt-2">

            <div className="row w-100 mb-2">
                <div className="col-1 d-flex flex-column justify-content-end">
                    {/*
                    <div className="mt-auto">
                        <button type="button" className="btn btn-outline-primary btn-sm mb-2" id="abrir" onClick={handleBtnNew}>+ New</button>
                    </div>
                    */}
                </div>
            </div >

            <div className="custom-data-grid" style={{ width: '90%', position: 'relative' }}>
                <DataGrid

                    slotProps={{
                        pagination: {
                            showFirstButton: true,
                            showLastButton: true,
                        },
                    }}

                    rows={rows}
                    columns={columns}
                    rowHeight={40}
                    disableRowSelectionOnClick={true}
                    autoHeight={true}
                    paginationMode="server"
                    sortingMode="server"
                    density="compact"
                    rowCount={totalRows}
                    pagination

                    pageSizeOptions={[20, 40, 60]}

                    onCellClick={(params) => {
                        console.log("===cell click===", params);
                    }}


                    paginationModel={paginationModel}


                    onPaginationModelChange={(model) => {
                        console.log("Pagination changed:", model); // Add this for debugging
                        const newPagination = {
                            ...pagination,
                            page: model.page,
                            pageSize: model.pageSize
                        };

                        setPagination(newPagination);
                        // Update the ref immediately to ensure ViewRow gets the latest value
                        paginationRef.current = newPagination;
                    }}


                    initialState={{
                        pagination: { paginationModel: { page: 0, pageSize: 20 } },
                        // Hide column product_id, the other columns will remain visible
                        columns: { columnVisibilityModel: { product_id: false } },

                    }}

                    onSortModelChange={handleSortModelChange}

                    loading={loading}
                    disableColumnFilter // If you're not using filtering
                    disableColumnSelector
                    disableDensitySelector


                />
                <DeleteConfirmation showModal={displayConfirmationModal}
                    confirmModal={() => handleSubmitDelete(id)}
                    hideModal={hideConfirmationModal} type={type} id={id} message={deleteMessage} />


                <OkModal
                    showModal={showOkModal}
                    hideModal={handleHideOkModal}
                    message={okMessage}
                    type={typeOkModal}
                    header=""
                />

                {loading && (
                    <div className="overlay">
                        <div className="loading-circle"></div>
                    </div>
                )}



            </div>
        </div >
    );
}


export default Products






